import { type ReactElement, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    EyeIcon,
    EyeOffIcon,
    H5Emphasized,
    Icon,
    isWeb,
    Private,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../common/misc';
import { maskValue } from '../../../../common/utils/bankingUtils';
import { formatWithoutTimezone } from '../../../../common/utils/dateUtils';
import {
    formatFullName,
    formatPhoneNumber,
    formatSsn,
} from '../../../../common/utils/userUtils';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import LegacyBankingTerms from '../../LegacyBankingTerms';
import KycButton from './KycButton';

interface ReviewProps {
    kycData: RunKYCVerifyBody;
    onPressSubmit: () => void;
}

const Review = ({ kycData, onPressSubmit }: ReviewProps) => {
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [hideSsn, setHideSsn] = useState(true);

    const {
        firstName,
        lastName,
        email,
        phoneNumber,
        dateOfBirth,
        ssn,
        line1,
        line2,
        state,
        city,
        postalCode,
    } = kycData;

    const formattedSsn = formatSsn(ssn);

    const ssnMasked = maskValue({
        value: formattedSsn,
        maskedLength: formattedSsn.length - 4,
        maskedCharacter: '•',
    });

    return (
        <YStack
            flex={1}
            justifyContent={isWeb ? 'flex-start' : 'space-between'}
        >
            <View>
                <H5Emphasized marginBottom={8}>Review</H5Emphasized>
                <BodyMediumRegular color="$gray900" marginBottom={24}>
                    We&apos;ll use these details to confirm your identity.
                </BodyMediumRegular>
                <YStack
                    borderWidth={1}
                    borderColor="$gray100"
                    borderRadius={12}
                    padding={16}
                >
                    <ReviewItem
                        label="Name"
                        value={formatFullName(firstName, lastName)}
                    />
                    <ReviewItem label="Email" value={email} />
                    <ReviewItem
                        label="Phone"
                        value={formatPhoneNumber(phoneNumber)}
                    />
                    <Private>
                        <ReviewItem
                            label="SSN"
                            value={
                                <>
                                    {hideSsn ? ssnMasked : formattedSsn}{' '}
                                    <Icon
                                        size={16}
                                        icon={hideSsn ? EyeIcon : EyeOffIcon}
                                    />
                                </>
                            }
                            onPress={() => {
                                setHideSsn(!hideSsn);
                            }}
                        />
                    </Private>
                    <Private>
                        <ReviewItem
                            label="DOB"
                            value={
                                dateOfBirth != null
                                    ? formatWithoutTimezone(
                                          dateOfBirth,
                                          'M-dd-yyyy',
                                      )
                                    : ''
                            }
                        />
                    </Private>
                    <ReviewItem
                        label="Address"
                        value={`${line1}${!isEmpty(line2) ? `, ${line2}` : ''}\n${city},${state != null ? ` ${state}` : ''} ${postalCode}`}
                        hideBorder
                    />
                </YStack>
                <LegacyBankingTerms
                    marginTop={24}
                    termsAccepted={termsAccepted}
                    onPress={() => {
                        setTermsAccepted(!termsAccepted);
                    }}
                />
            </View>
            <KycButton
                onPress={onPressSubmit}
                text="Confirm"
                disabled={!termsAccepted}
            />
        </YStack>
    );
};

interface ReviewItemProps {
    label: string;
    value: string | ReactElement;
    hideBorder?: boolean;
    onPress?: () => void;
}

const ReviewItem = ({
    label,
    value,
    hideBorder = false,
    onPress,
}: ReviewItemProps) => {
    return (
        <XStack
            alignItems="flex-start"
            justifyContent="space-between"
            borderBottomWidth={hideBorder ? 0 : 1}
            borderColor="$gray100"
            paddingVertical={12}
            onPress={onPress}
        >
            <BodyMediumRegular color="$gray900">{label}</BodyMediumRegular>
            <BodyMediumEmphasized textAlign="right">
                {value}
            </BodyMediumEmphasized>
        </XStack>
    );
};

export default Review;
